import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  DatabaseZap, Play, RotateCcw, Save, RefreshCw, Zap, Clock, Activity, AlertCircle,
} from "lucide-react";

const ARO_FALLBACK_TOTAL = 342101; // Aroflo total tasks — fallback if API hasn't reported yet

function TinyStat({ label, value, tone = "default", testid }) {
  const cls = tone === "good" ? "text-emerald-700"
           : tone === "warn" ? "text-amber-700"
           : tone === "danger" ? "text-rose-700"
           : "text-slate-900";
  return (
    <div data-testid={testid} className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className={`text-xl font-display font-semibold mt-1 ${cls}`}>{value}</div>
    </div>
  );
}

function formatRel(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const secs = Math.round((Date.now() - d.getTime()) / 1000);
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
    return d.toISOString().slice(0, 10);
  } catch { return iso; }
}

export function AroFloSyncPanel() {
  const [status, setStatus] = useState(null);
  const [interval, setIntervalMin] = useState(60);
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [busyIncr, setBusyIncr] = useState(false);
  const [savingSched, setSavingSched] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/aroflo/sync/deep-status");
      setStatus(r.data);
      setIntervalMin(r.data?.schedule?.interval_minutes ?? 60);
      setEnabled(r.data?.schedule?.enabled ?? true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load Aroflo sync status");
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // auto-refresh every 30s
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, []);

  const startDeep = async () => {
    setBusy(true);
    try {
      const r = await api.post("/aroflo/sync/deep-start");
      if (r.data.ok === false) toast.error(r.data.message || r.data.error);
      else toast.success(r.data.message || "Deep sync started");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start deep sync");
    } finally { setBusy(false); }
  };

  const stopDeep = async () => {
    try {
      const r = await api.post("/aroflo/sync/deep-stop");
      toast.success(r.data.message || "Stop requested");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to stop deep sync");
    }
  };

  const runIncremental = async () => {
    setBusyIncr(true);
    try {
      const r = await api.post("/aroflo/sync/incremental", {}, { timeout: 60000 });
      if (r.data.ok === false) toast.error(r.data.error || "Incremental sync failed");
      else toast.success(`Incremental sync — ${r.data.records_upserted} records upserted`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Incremental sync failed");
    } finally { setBusyIncr(false); }
  };

  const resetCursor = async () => {
    const isRunning = status?.state?.running;
    const msg = isRunning
      ? "The sync is marked as RUNNING. If it's genuinely stuck (e.g. after a deploy or crash), Reset will forcibly clear the lock and set the cursor to offset 0. Continue?"
      : "Reset the deep sync cursor to offset 0? Existing records stay in place.";
    if (!window.confirm(msg)) return;
    setResetting(true);
    try {
      await api.post("/aroflo/sync/deep-reset");
      toast.success("Deep sync cursor reset");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally { setResetting(false); }
  };

  const saveSchedule = async () => {
    setSavingSched(true);
    try {
      await api.put("/aroflo/sync/schedule",
        { interval_minutes: Number(interval), enabled });
      toast.success("Schedule saved");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save schedule");
    } finally { setSavingSched(false); }
  };

  const st = status?.state || {};
  const sch = status?.schedule || {};
  const jobsInHub = status?.aroflo_jobs_in_hub ?? 0;
  const configured = status?.aroflo_v2_configured;
  const aroTotal = st.aroflo_total || ARO_FALLBACK_TOTAL;
  const progressPct = Math.min(100, Math.round((jobsInHub / aroTotal) * 100));

  // Live sync rate + ETA — sampled from consecutive status polls (30s apart)
  const prevSample = useRef(null);
  const [eta, setEta] = useState(null);
  useEffect(() => {
    const offset = st.offset ?? 0;
    const now = Date.now();
    const prev = prevSample.current;
    if (st.running && prev && offset > prev.offset && now > prev.t) {
      const perSec = (offset - prev.offset) / ((now - prev.t) / 1000);
      const remaining = Math.max(0, aroTotal - offset);
      if (perSec > 0) {
        const mins = remaining / perSec / 60;
        setEta({ perMin: Math.round(perSec * 60), mins });
      }
    }
    if (!st.running) setEta(null);
    prevSample.current = { offset, t: now };
    // eslint-disable-next-line
  }, [st.offset, st.running]);
  const etaLabel = eta
    ? eta.mins > 90 ? `~${(eta.mins / 60).toFixed(1)} h left` : `~${Math.ceil(eta.mins)} min left`
    : null;

  const runningBadge = st.running
    ? <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Running</Badge>
    : st.completed
      ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Completed</Badge>
      : <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">Idle</Badge>;

  return (
    <div className="space-y-4" data-testid="aroflo-sync-panel">
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2 mb-4">
          <DatabaseZap className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-semibold text-slate-900">Aroflo Deep Sync</span>
          {runningBadge}
          {!configured && (
            <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 ml-1">v2 not configured</Badge>
          )}
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={load} data-testid="aro-sync-refresh">
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Refresh
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 mb-4">
          <TinyStat testid="aro-stat-jobs" label="Records in Hub" value={jobsInHub.toLocaleString()} />
          <TinyStat testid="aro-stat-offset" label="Cursor Offset" value={(st.offset ?? 0).toLocaleString()} />
          <TinyStat testid="aro-stat-total" label="Aroflo Total" value={aroTotal.toLocaleString()} />
          <TinyStat testid="aro-stat-progress" label="Progress"
            value={`${progressPct}%`}
            tone={progressPct >= 100 ? "good" : progressPct >= 50 ? "default" : "warn"} />
          {st.running && (
            <TinyStat testid="aro-stat-eta" label="Rate / ETA"
              value={eta ? `${eta.perMin.toLocaleString()}/min · ${etaLabel}` : "measuring…"} />
          )}
        </div>

        <div className="mb-4">
          <div className="h-2 bg-slate-100 rounded overflow-hidden">
            <div
              className={`h-full transition-all ${progressPct >= 100 ? "bg-emerald-500" : "bg-blue-500"}`}
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs mb-4">
          <div className="text-slate-600">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Deep run started</div>
            <div className="mt-0.5 flex items-center gap-1"><Clock className="w-3 h-3" /> {formatRel(st.last_deep_run_started_at)}</div>
          </div>
          <div className="text-slate-600">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Deep run finished</div>
            <div className="mt-0.5 flex items-center gap-1"><Clock className="w-3 h-3" /> {formatRel(st.last_deep_run_finished_at)}</div>
          </div>
          <div className="text-slate-600">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">Last incremental</div>
            <div className="mt-0.5 flex items-center gap-1"><Activity className="w-3 h-3" /> {formatRel(st.last_incremental_at)}</div>
          </div>
        </div>

        {st.last_error && (
          <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-700 flex items-start gap-1.5">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span><strong>Last error:</strong> {st.last_error}</span>
          </div>
        )}
        {st.stale_lock && (
          <div className="mb-3 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900 flex items-start gap-1.5">
            <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              <strong>Stale lock detected</strong> — the sync says it's running but hasn't checked in for 5+ minutes.
              This usually means a previous run was killed mid-flight (deploy / OOM). Click <b>Reset cursor</b> to
              clear the lock, then <b>Resume deep sync</b> to continue from offset {(st.offset ?? 0).toLocaleString()}.
            </span>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={startDeep} disabled={busy || !configured || (st.running && !st.stale_lock) || st.completed}
            data-testid="aro-start-deep">
            <Play className="w-3.5 h-3.5 mr-1.5" />
            {st.completed ? "Deep sync complete" : st.running && !st.stale_lock ? "Running…" : (st.offset ?? 0) > 0 ? "Resume deep sync" : "Start deep sync"}
          </Button>
          {st.running && !st.stale_lock && (
            <Button size="sm" variant="outline" onClick={stopDeep}
              className="text-rose-700 border-rose-200 hover:bg-rose-50"
              data-testid="aro-stop-deep">
              <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
              Stop deep sync
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={runIncremental} disabled={busyIncr || !configured}
            data-testid="aro-run-incremental">
            <Zap className={`w-3.5 h-3.5 mr-1.5 ${busyIncr ? "animate-pulse" : ""}`} />
            Run incremental now
          </Button>
          <Button size="sm" variant="ghost" onClick={resetCursor} disabled={resetting}
            className={st.stale_lock ? "text-white bg-amber-600 hover:bg-amber-700" : "text-rose-700 hover:text-rose-800"}
            data-testid="aro-reset-cursor">
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
            {st.stale_lock ? "Force-clear stale lock" : "Reset cursor"}
          </Button>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-semibold text-slate-900">Incremental Sync Schedule</span>
          {sch.enabled ? (
            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Enabled</Badge>
          ) : (
            <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100">Disabled</Badge>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div>
            <Label htmlFor="sched-interval" className="text-xs">Cadence (minutes)</Label>
            <Input id="sched-interval" type="number" min={5} max={1440} value={interval}
              data-testid="aro-schedule-interval"
              onChange={(e) => setIntervalMin(e.target.value)} />
            <div className="text-[10px] text-slate-500 mt-1">Range 5–1440. Default 60 (hourly).</div>
          </div>
          <div className="flex items-center gap-3">
            <Switch id="sched-enabled" checked={enabled}
              onCheckedChange={setEnabled} data-testid="aro-schedule-enabled" />
            <Label htmlFor="sched-enabled" className="text-sm text-slate-700 cursor-pointer">
              Enable scheduled incremental syncs
            </Label>
          </div>
          <div>
            <Button size="sm" onClick={saveSchedule} disabled={savingSched}
              data-testid="aro-schedule-save">
              <Save className={`w-3.5 h-3.5 mr-1.5 ${savingSched ? "animate-pulse" : ""}`} />
              Save schedule
            </Button>
          </div>
        </div>

        <div className="mt-4 text-xs text-slate-500 leading-relaxed">
          The scheduler fetches the newest page (up to 100 Aroflo tasks per Aroflo's v2 hard limit)
          each run and upserts them into the Hub. Deep sync uses the same page-based pagination
          and writes into the same collection, so KPIs update in-place. All sync calls are routed
          through the API Governance layer and honour the kill switch.
        </div>
      </div>
    </div>
  );
}
